(ns common.schema.entities.counterparty-diligence.templates.capman.legal-entity
  (:require
   [common.schema.entities.counterparty-diligence.templates.capman.board-member :as board-member]
   [common.schema.entities.counterparty-diligence.templates.capman.ubo :as ubo]
   [common.schema.entities.counterparty-diligence.templates.questions.legal-entity :as legal-entity-q]
   [common.schema.entities.counterparty-diligence.templates.questions.person :as person-q]
   [common.schema.entities.counterparty-diligence.templates.questions.common :as common-q]
   [common.schema.entities.counterparty-diligence.templates.capman.ceo :as ceo]
   [common.schema.entities.counterparty-diligence.templates.capman.pep :as pep]
   [common.schema.entities.counterparty-diligence.templates.util :as tu]
   [common.schema.entities.counterparty-diligence.templates.validation :as validation]
   [clojure.string :as string]
   [clojure.set :as set]))

(def yes-no-options
  [{:value :yes
    :label "Yes"}
   {:value :no
    :label "No"}])

(def template-id :counterparty-diligence-template/capman-legal-entity)

(defn find-key-persons
  [{:keys [data]}]
  ;; NOTE: Capman uses :person/first-names (plural) in their templates,
  ;; but we normalize to :first-name (singular) for screening profile compatibility.
  (let [with-countries (fn [person]
                         ;; resolved with the legacy single nationality
                         ;; fallback; screening supports several countries
                         (let [countries (tu/nationality-countries person)]
                           (assoc person
                                  :country (first countries)
                                  :countries countries)))
        ceo (some->> data
                     :ceo
                     (mapv (fn [person]
                             (-> person
                                 with-countries
                                 (set/rename-keys
                                  {:person/first-names   :first-name
                                   :person/last-name     :last-name
                                   :person/date-of-birth :date-of-birth})
                                 (assoc :roles #{:ceo})))))
        board-members (some->> data
                               :board-members
                               (mapv (fn [person]
                                       (-> person
                                           with-countries
                                           (set/rename-keys
                                            {:person/first-names   :first-name
                                             :person/last-name     :last-name
                                             :person/date-of-birth :date-of-birth})
                                           (assoc :roles #{(:board-member/role person)})))))

        ubos (some->> data
                      :ultimate-beneficial-owners
                      (mapv (fn [person]
                              (-> person
                                  with-countries
                                  (set/rename-keys
                                   {:person/first-names   :first-name
                                    :person/last-name     :last-name
                                    :person/date-of-birth :date-of-birth})
                                  (assoc :roles #{:ultimate-beneficial-owner}
                                         :is-pep? (-> person
                                                      :person-is-politically-exposed
                                                      (= :yes)))))))

        peps (some->> data
                      :politically-exposed-persons
                      (mapv (fn [person]
                              (-> person
                                  (set/rename-keys {:pep/first-names :first-name
                                                    :pep/last-name   :last-name})
                                  (assoc :roles #{:politically-exposed-person}
                                         :is-pep? true)))))

        persons (->> (concat ceo
                             board-members
                             ubos
                             peps)
                     (mapv (fn [{:keys [first-name last-name]
                                 :as person}]
                             (assoc person
                                    :first-name (-> first-name
                                                    str
                                                    string/trim)
                                    :last-name (-> last-name
                                                   str
                                                   string/trim))))
                     (filterv (fn [{:keys [first-name last-name]}]
                                (and (seq first-name)
                                     (seq last-name)))))]
    persons))

(def schema
  {:template/id template-id
   :template/template-name "Legal Entity"

   :template/form-title    {:en "Transaction Counterparty KYC"}

   :template/notifications
   [{:on-status       :counterparty-diligence.status/review
     :notification-id :notification/counterparty-diligence-review}]

   :template/form-preface
   {:en (str "<p>In accordance with Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) regulations, CapMan Group is required to collect specific Know Your Customer (KYC) information on all transaction counterparties.</p>"
             "<br/>"
             "<p>As part of the legal due diligence process, the nominated counsel is requested to gather and review the required documentation of all counterparties of the transaction, and to promptly report any suspicious findings or material observations to CapMan.</p>"
             "<br/>"
             "<p>The Transaction Counterparty KYC check must be carried out on all transaction counterparties:</p>"
             "<ul>"
             "<li>to whom ownership/assets are transferred <strong>directly from CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction</li>"
             "<li>from whom ownership/assets are transferred <strong>directly to CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction.</li>"
             "</ul>"
             "<p>Please complete all required information, attach all reviewed materials, and finally confirm the accuracy of the information to the best of your knowledge by signing this form.</p>")}

   :template/find-key-persons find-key-persons

   :template/schema
   (merge
    (->> [legal-entity-q/company-name
          legal-entity-q/business-id
          legal-entity-q/jurisdiction
          legal-entity-q/trade-register-extract-or-eq
          legal-entity-q/articles-of-association-or-equivalent-or-eq]
         (mapv (fn [{:keys [field] :as config}]
                 [field config]))
         (into {}))

    {(:field legal-entity-q/company-legal-form)
     (merge
      legal-entity-q/company-legal-form
      {:linked-fields [(:field legal-entity-q/company-legal-form-other)]})}

    {(:field legal-entity-q/company-legal-form-other)
     (merge
      legal-entity-q/company-legal-form-other
      {:validation-fn (fn [{:keys [value data draft?]}]
                        (let [legal-form-other? (->> legal-entity-q/company-legal-form
                                                     :field
                                                     (get data)
                                                     (= :legal-form/other))
                              mandatoriness (if (or draft?
                                                    (not legal-form-other?))
                                              :mandatory/optional
                                              :mandatory/always)]
                          (validation/validate-text-field
                           {:field-config {:mandatoriness mandatoriness}}
                           value)))
       :visibility-fn (fn [{:keys [data]}]
                        (->> legal-entity-q/company-legal-form
                             :field
                             (get data)
                             (= :legal-form/other)))})}

    {:ownership-structure-chart-showing-beneficial-owners
     {:label     {:en "Ownership structure chart showing beneficial owners (up to individuals)"}
      :data-type :data.type/file}

     :shareholder-register
     {:label     {:en "Shareholder register (including share and voting rights)"}
      :data-type :data.type/file}

     :latest-financial-accounts
     {:label     {:en "Latest financial accounts"}
      :data-type :data.type/file
      :info-text-position :bottom
      :info-text {:en "If the company was recently founded and hence, no financial statements are available, please attach the company's incorporation document."
                  :fi "Jos yritys on perustettu hiljattain eikä sen tilinpäätöstä ole vielä saatavilla, liitä yrityksen perustamisasiakirja."}}

     :information-on-the-source-of-funds
     {:label     {:en "Source of funds and wealth"}
      :data-type :data.type/text-multiline}

     :ceo
     {:label          {:en "Chief Executive Officer"}
      :entity-label   {:en "Chief Executive Officer"}
      :data-type      :data.type/vector
      :item-data-type :data.type/map
      :editor-type    :editor.type/entity-list
      :item-template  ceo/schema
      :set-as-ubo     {:target-field         :ultimate-beneficial-owners
                       :default-ubo-category :mob-ceo-or-similar
                       :map-fields           [:person/first-names :person/middle-names
                                              :person/last-name
                                              :person/date-of-birth :person/nationality
                                              :person/nationalities
                                              :person/type-of-identification-attachment
                                              :person/identification-expiration-date
                                              :person/passport-photo-page-file
                                              :person/id-card-front-file
                                              :person/id-card-back-file]}
      :table-columns  [person-q/first-names-singular-label
                       person-q/last-name
                       person-q/date-of-birth
                       person-q/nationalities]
      :item-display-string-fields [person-q/first-names
                                   person-q/last-name]}

     :board-members
     {:label         {:en "Board members"}
      :entity-label  {:en "Board member"}
      :entity-label-plural {:en "Board members"}
      :data-type     :data.type/vector
      :editor-type   :editor.type/entity-list
      :item-data-type :data.type/map
      :min-items     2
      :info-text     {:en "Add all board members. If the board only includes one member, please add at least one deputy member."}
      :item-template board-member/schema
      :set-as-ubo    {:target-field         :ultimate-beneficial-owners
                      :default-ubo-category :mob-ceo-or-similar
                      :map-fields           [:person/first-names :person/middle-names
                                             :person/last-name
                                             :person/date-of-birth :person/nationality
                                             :person/nationalities
                                             :person/type-of-identification-attachment
                                             :person/identification-expiration-date
                                             :person/passport-photo-page-file
                                             :person/id-card-front-file
                                             :person/id-card-back-file]}
      :table-columns [person-q/first-names-singular-label
                      person-q/last-name
                      board-member/role
                      person-q/date-of-birth
                      person-q/nationalities]
      :item-display-string-fields [person-q/first-names
                                   person-q/last-name]}

     :ultimate-beneficial-owners
     {:label         {:en "Actual Beneficial Owners or Alternative Beneficial Owners"}
      :entity-label  {:en "Beneficial Owner"}
      :entity-label-plural {:en "Beneficial Owners"}
      :data-type     :data.type/vector
      :editor-type   :editor.type/entity-list
      :item-data-type :data.type/map
      :min-items      1
      :item-template  ubo/schema
      :table-columns [person-q/first-names-singular-label
                      person-q/last-name
                      person-q/date-of-birth
                      person-q/nationalities]
      :item-display-string-fields [person-q/first-names
                                   person-q/last-name]}

     :company-has-politically-exposed-persons
     {:label         {:en "Does the company have politically exposed person(s)?"}
      :data-type     :data.type/keyword
      :options       yes-no-options
      :linked-fields [:politically-exposed-persons]}

     :politically-exposed-persons
     {:label            {:en "Politically exposed persons"}
      :entity-label     {:en "Politically exposed person"}
      :visibility-fn    (fn [{:keys [data]}]
                          (-> data
                              (get :company-has-politically-exposed-persons)
                              (= :yes)))
      :data-type        :data.type/vector
      :item-data-type   :data.type/map
      :item-template    pep/schema
      :editor-type      :editor.type/entity-list
      :table-columns    (->> [:pep/first-names
                              :pep/last-name]
                             (mapv (fn [field]
                                     (-> pep/schema
                                         (get-in [:template/schema field])
                                         (assoc :field field)))))
      :item-display-string-fields (->> [:pep/first-names
                                        :pep/last-name]
                                       (mapv (fn [field]
                                               (-> pep/schema
                                                   (get-in [:template/schema field])
                                                   (assoc :field field)))))}}

    {(:field common-q/signatories) common-q/signatories})

   :template/sections
   {:basic-information
    {:ord   1
     :title {:en "Basic information"}
     :fields (->> [legal-entity-q/company-name
                   legal-entity-q/business-id
                   legal-entity-q/jurisdiction
                   legal-entity-q/company-legal-form
                   legal-entity-q/company-legal-form-other
                   legal-entity-q/trade-register-extract-or-eq
                   legal-entity-q/articles-of-association-or-equivalent-or-eq]
                  (mapv :field))}

    :organization-and-ownership-details
    {:ord   2
     :title {:en "Organization and ownership details"}
     :fields [:ownership-structure-chart-showing-beneficial-owners
              :shareholder-register]}

    :financial-information
    {:ord   3
     :title {:en "Financial information"}
     :info-text-position :top
     :fields [:latest-financial-accounts
              :information-on-the-source-of-funds]}

    :key-persons
    {:ord   4
     :title {:en "Key persons"}
     :fields [:ceo
              :board-members
              :ultimate-beneficial-owners]}

    :politically-exposed-persons
    {:ord   5
     :title {:en "Politically exposed persons"}
     :fields [:company-has-politically-exposed-persons
              :politically-exposed-persons]}

    :signatories
    {:ord                6
     :title              {:en "Signatories"}
     :info-text {:en (str "<p>If you have the authority to sign this form, please fill in your name and email below.</p>"
                          "<br/>"
                          "<p>If you do not have the authority to sign this form, please fill in the name and email address of the person from your company who does have the authority to sign.</p>")}
     :info-text-position :top
     :signatory-section? true
     :fields             [:signatories]}}})
